/**
 * Eksport/import menu tenanta do pliku (przenoszenie menu między tenantami).
 *
 * Plik niesie strukturę menu (pozycje, sekcje, nazwy, ikony, kolejność) — BEZ rzeczy
 * przypiętych do tenanta źródłowego: układów przypiętych do pozycji, filtrów stałych,
 * przypisań menu do ról/zespołów/użytkowników. Import u tenanta docelowego klasyfikuje
 * każdą pozycję na nowo względem JEGO danych i zwraca raport „czego nie wczytamy".
 */

import { MENU_LIMITS, type MenuItem } from './menu';

/**
 * Maksymalny rozmiar pliku/żądania importu (bajty): menu (limit zapisu 1 MB) + koperta pliku
 * i podpowiedzi. Jeden limit dla serwera (413 przed parsowaniem) i przeglądarki (odrzucenie
 * pliku przed odczytem).
 */
export const MENU_IMPORT_MAX_BYTES = MENU_LIMITS.MAX_SERIALIZED_BYTES + 256 * 1024;

/** Znacznik formatu pliku — odróżnia plik menu od dowolnego JSON-a. */
export const MENU_EXPORT_FORMAT = 'workflow365-menu' as const;

/** Wersja formatu. Import odrzuca wersje nowsze niż obsługiwana. */
export const MENU_EXPORT_VERSION = 1;

/** Skąd pochodzi cel pozycji `module` u tenanta źródłowego (tylko do raportu). */
export type MenuExportTargetOrigin = 'system' | 'creator' | 'custom';

/**
 * Podpowiedź do raportu importu o pozycji z pliku (klucz = id pozycji w pliku).
 * Import NIE ufa podpowiedziom przy decyzji „wczytać" — mogą tylko zaostrzyć ocenę
 * (pozycja oznaczona jako kartoteka własna źródła jest zawsze pomijana).
 */
export interface MenuExportItemHint {
  /** Czytelna nazwa celu u tenanta źródłowego (np. nazwa kartoteki). */
  targetLabel?: string;
  origin?: MenuExportTargetOrigin;
  /** Pozycja miała przypięty układ formularza lub listy (wycięty przy eksporcie). */
  hadLayout?: boolean;
  /** Pozycja miała filtry/sortowanie stałe (wycięte przy eksporcie). */
  hadFilters?: boolean;
}

/** Zawartość pliku eksportu menu. */
export interface MenuExportFile {
  format: typeof MENU_EXPORT_FORMAT;
  version: number;
  exportedAt: string;
  menu: {
    name: string;
    description: string | null;
    icon: string | null;
    color: string | null;
    hierarchy: MenuItem[];
  };
  hints: Record<string, MenuExportItemHint>;
}

/** Wynik oceny pojedynczej pozycji przy imporcie. */
export type MenuImportItemStatus = 'loaded' | 'loaded_without_layout' | 'skipped';

/** Dlaczego pozycja nie zostanie wczytana. */
export type MenuImportSkipReason =
  /** Kartoteka własna tenanta źródłowego — u docelowego to inne dane (nawet przy tym samym kodzie). */
  | 'custom_kartoteka'
  /** Moduł/kartoteka, której tenant docelowy nie ma (niewykupiona albo nieznana). */
  | 'target_unavailable'
  /** Obszar bez wykupionych modułów u tenanta docelowego. */
  | 'area_unavailable'
  /** Odnośnik do konkretnych danych tenanta źródłowego (identyfikator rekordu/typu w adresie). */
  | 'link_to_source_data'
  /** Nieprawidłowy albo niebezpieczny adres (np. `//obca.pl`, uszkodzone kodowanie). */
  | 'invalid_link'
  /** Odnośnik do ekranu modułu, którego tenant docelowy nie ma. */
  | 'link_unavailable'
  /** Sekcja, która po pominięciach została pusta. */
  | 'empty_section';

/** Co zostało zdjęte z wczytanej pozycji. */
export type MenuImportItemNote = 'layout_dropped' | 'filters_dropped';

export interface MenuImportReportEntry {
  /** Etykiety sekcji nadrzędnych (ścieżka w drzewie), od korzenia. */
  path: string[];
  label: string;
  type: MenuItem['type'];
  status: MenuImportItemStatus;
  reason?: MenuImportSkipReason;
  notes?: MenuImportItemNote[];
  /** Adres wczytywanego odnośnika zewnętrznego — pokazywany w raporcie przed zapisem. */
  url?: string;
}

export interface MenuImportReport {
  entries: MenuImportReportEntry[];
  counts: { loaded: number; loadedWithoutLayout: number; skipped: number };
}

/** Odpowiedź podglądu importu (`POST /api/menu/layouts/import?dryRun=true`). */
export interface MenuImportPreviewResponse {
  report: MenuImportReport;
  /** Nazwa, pod którą menu zostanie zapisane (wolna — z dopiskiem przy kolizji). */
  proposedName: string;
  /** Czy po pominięciach zostaje coś do wczytania. */
  importable: boolean;
  /** Menu w postaci, w jakiej zostanie zapisane (bez pominiętych pozycji) — do podglądu. */
  hierarchy: MenuItem[];
}
